import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Monitor, SignOut } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

/**
 * Signed-in devices panel — shown in Settings.
 *
 * Backend enforces single-session for regular_user + tenant_admin, so this
 * panel will surface exactly one row (the caller's own current session).
 * For super_admin the backend returns `tracked: false` and we render a
 * friendly note explaining device tracking is disabled for admin accounts.
 *
 * Parsing user_agent is deliberately kept simple: we surface the raw UA
 * only as a fallback tooltip and try to summarise the browser + OS from
 * the first well-known token. A heavy UA parser (like `ua-parser-js`)
 * would blow up the bundle for a one-row panel; keeping it inline is fine.
 */
function summariseUA(ua) {
  if (!ua) return { device: 'Unknown device', detail: '' };
  const browser =
    /Firefox\/(\d+)/.exec(ua) ? `Firefox ${RegExp.$1}` :
    /Edg\/(\d+)/.exec(ua) ? `Edge ${RegExp.$1}` :
    /Chrome\/(\d+)/.exec(ua) ? `Chrome ${RegExp.$1}` :
    /Safari\/(\d+)/.exec(ua) ? 'Safari' :
    'Browser';
  const os =
    /Windows NT ([\d.]+)/.exec(ua) ? 'Windows' :
    /Mac OS X ([\d_]+)/.exec(ua) ? 'macOS' :
    /Android (\d+)/.exec(ua) ? `Android ${RegExp.$1}` :
    /iPhone|iPad|iOS/.test(ua) ? 'iOS' :
    /Linux/.test(ua) ? 'Linux' :
    'OS';
  return { device: `${browser} on ${os}`, detail: ua };
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function SignedInDevicesPanel() {
  const { apiCall, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  // Confirm dialog before actually logging out — matches the themed
  // confirmation flow the rest of the app uses (no browser-native prompts).
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('get', '/auth/sessions');
      setData(res?.data ?? { tracked: true, sessions: [] });
    } catch (_) {
      setData({ tracked: true, sessions: [] });
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { refresh(); }, [refresh]);

  const performSignOut = async () => {
    setConfirmOpen(false);
    setSigningOut(true);
    try {
      await logout();
      // logout() clears local user; router will bounce to /login.
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Card className="shadow-card rounded-lg border-0" data-testid="signed-in-devices-card">
      <CardContent className="p-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
          <Monitor size={14} className="inline mr-1.5" weight="bold" />Signed-in devices
        </h2>

        {loading && (
          <div className="text-xs text-muted-foreground" data-testid="signed-in-devices-loading">
            Loading…
          </div>
        )}

        {!loading && data && !data.tracked && (
          <div
            className="text-xs text-muted-foreground"
            data-testid="signed-in-devices-not-tracked"
          >
            {data.reason || 'Device tracking is not enabled for this account.'}
          </div>
        )}

        {!loading && data && data.tracked && data.sessions.length === 0 && (
          <div
            className="text-xs text-muted-foreground"
            data-testid="signed-in-devices-empty"
          >
            No active sessions on record.
          </div>
        )}

        {!loading && data && data.tracked && data.sessions.map((s, idx) => {
          const ua = summariseUA(s.user_agent);
          return (
            <div
              key={idx}
              className="flex items-start justify-between gap-4 py-3 border-t border-border first:border-t-0"
              data-testid="signed-in-device-row"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate" title={ua.detail}>
                    {ua.device}
                  </span>
                  {s.is_current && (
                    <span className="px-1.5 py-px rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold uppercase tracking-wider">
                      Current
                    </span>
                  )}
                </div>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                  <span data-testid="signed-in-device-ip">IP&nbsp;·&nbsp;{s.ip || 'Unknown'}</span>
                  <span data-testid="signed-in-device-started">Signed in&nbsp;·&nbsp;{fmtTime(s.started_at)}</span>
                  <span data-testid="signed-in-device-last-seen">Last seen&nbsp;·&nbsp;{fmtTime(s.last_seen_at)}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={signingOut}
                className="shrink-0"
                data-testid="signed-in-device-signout"
              >
                <SignOut size={14} weight="bold" className="mr-1.5" />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </Button>
            </div>
          );
        })}

        <ConfirmDeleteDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Sign out of this device?"
          description="You'll need to log in again to keep using RoofMate on this browser. Any other devices you're signed in on will remain active."
          onConfirm={performSignOut}
          destructiveLabel="Sign out"
        />
      </CardContent>
    </Card>
  );
}
